/**
 * The group-chat FIXTURE — the same two groups and the same "Launch crew" transcript the phones seed under
 * `SYMPHONY_CHAT_SEED_GROUP=1` (native-apps/docs/claude/launch-hooks.md). Read by the unit tests and by the
 * dev Mock data panel's "Group chats" row, never by a real session. All avatars are initials, so it
 * needs no network.
 */
import type { GroupEvent, GroupSummary, MessageAuthor } from './group-chat';

export const GROUP_SEED_LAUNCH_ID = 'group-seed-launch';
export const GROUP_SEED_UNNAMED_ID = 'group-seed-unnamed';

export const GROUP_SEED_LAUNCH: GroupSummary = {
  name: 'Launch crew',
  memberCount: 6,
  viewerId: 'ms-ronny',
  lastMessageAuthorId: 'ms-dana',
  participants: [
    { id: 'ms-ronny', kind: 'HUMAN', displayName: 'Ronny Elkayam', role: 'OWNER' },
    { id: 'ms-dana', kind: 'HUMAN', displayName: 'Dana Levi', role: 'ADMIN' },
    { id: 'ms-amir', kind: 'HUMAN', displayName: 'Amir Cohen', role: 'MEMBER' },
    { id: 'ms-noa', kind: 'HUMAN', displayName: 'Noa Bar', role: 'MEMBER' },
    // The viewer's own Maestro (its owner is the viewer): drawn in the stack and labelled "Maestro · your assistant".
    { id: 'agent-maestro-ronny', kind: 'MAESTRO', displayName: 'Maestro', role: 'MEMBER', ownerId: 'ms-ronny', ownerFirstName: 'Ronny', mine: true },
    { id: 'agent-atlas', kind: 'MAESTRO', displayName: 'Atlas', role: 'MEMBER', ownerId: 'ms-dana', ownerFirstName: 'Dana' },
    // Phase 5: a member's own Maestro, sent with no name → "Amir's Maestro".
    { id: 'agent-maestro-amir', kind: 'MAESTRO', displayName: '', role: 'MEMBER', ownerId: 'ms-amir', ownerFirstName: 'Amir' },
  ],
};

export const GROUP_SEED_UNNAMED: GroupSummary = {
  name: '',
  memberCount: 3,
  viewerId: 'ms-ronny',
  lastMessageAuthorId: 'agent-maestro-dana',
  participants: [
    { id: 'ms-ronny', kind: 'HUMAN', displayName: 'Ronny Elkayam', role: 'OWNER' },
    { id: 'ms-dana', kind: 'HUMAN', displayName: 'Dana Levi', role: 'MEMBER' },
    { id: 'agent-maestro-dana', kind: 'MAESTRO', displayName: 'Maestro', role: 'MEMBER', ownerId: 'ms-dana', ownerFirstName: 'Dana' },
  ],
};

export const GROUP_SEED_LAUNCH_PREVIEW = 'Can we move the launch to Tuesday?';
export const GROUP_SEED_UNNAMED_PREVIEW = 'I found three free slots on Thursday.';

export interface GroupSeedMessage {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  author?: MessageAuthor;
  groupEvent?: GroupEvent;
  /** The wire `metadata` (Phase 5: `groupApproval`, `maestroUnavailable`), read through `lib/group-maestro.ts`. */
  metadata?: Record<string, unknown>;
  /** Phase 7: a file shared into the group, by reference (`groupFile`, url empty). */
  attachments?: { fileName: string; mimeType: string; url: string; groupFile: { conversationId: string; fileId: string; ownerMetaSiteId: string } }[];
}

const human = (id: string): MessageAuthor => ({ principalMetaSiteId: id, kind: 'HUMAN' });
/** A Maestro's message: the principal is its OWNER's metasite — the same id as the owner's human row. */
const maestro = (owner: string): MessageAuthor => ({ principalMetaSiteId: owner, kind: 'MAESTRO', agentOwnerMetaSiteId: owner });

/** Phase 7: the three group files the fixture shares (`GROUP_SEED_FILES`), as the chat card's reference. */
export const GROUP_SEED_FILES = [
  { fileId: 'mock-group-file-1', fileName: 'launch-plan.md', ownerMetaSiteId: 'ms-dana', ownerName: 'Dana Levi', permission: 'EDIT', available: true, content: '# Launch plan\n\n- Tuesday 10:00 kickoff\n- Assets from marketing by Monday\n' },
  { fileId: 'mock-group-file-2', fileName: 'prices.csv', ownerMetaSiteId: 'ms-amir', ownerName: 'Amir Cohen', permission: 'READ', available: true, content: 'Item,Cost\nAds,$1200\nPrint,$400\n' },
  { fileId: 'mock-group-file-3', fileName: 'press-brief.md', ownerMetaSiteId: 'ms-noa', ownerName: 'Noa Bar', permission: 'EDIT', available: false, content: '' },
  // Phase 8 (Phase 7 follow-up): one unavailable row per remaining reason, so the Files list shows each caption.
  // press-brief.md above names no reason (the generic caption); the same rows on iOS / Android.
  { fileId: 'mock-group-file-4', fileName: 'budget.md', ownerMetaSiteId: 'ms-dana', ownerName: 'Dana Levi', permission: 'EDIT', available: false, unavailableReason: 'REVOKED', content: '' },
  { fileId: 'mock-group-file-5', fileName: 'old-agenda.md', ownerMetaSiteId: 'ms-amir', ownerName: 'Amir Cohen', permission: 'EDIT', available: false, unavailableReason: 'DELETED', content: '' },
  { fileId: 'mock-group-file-6', fileName: 'vendor-list.md', ownerMetaSiteId: 'ms-yael', ownerName: 'Yael Shani', permission: 'EDIT', available: false, unavailableReason: 'OWNER_GONE', content: '' },
  { fileId: 'mock-group-file-7', fileName: 'scan.md', ownerMetaSiteId: 'ms-noa', ownerName: 'Noa Bar', permission: 'EDIT', available: false, unavailableReason: 'UNREADABLE', content: '' },
] as const;
function groupFileAttachment(i: number) {
  const f = GROUP_SEED_FILES[i];
  return { fileName: f.fileName, mimeType: f.fileName.endsWith('.csv') ? 'text/csv' : 'text/markdown', url: '', groupFile: { conversationId: 'mock-conversation-group-1', fileId: f.fileId, ownerMetaSiteId: f.ownerMetaSiteId } };
}
const inADay = () => new Date(Date.now() + 24 * 3_600_000).toISOString();

const media = (mediaId: string, mimeType: string, sizeBytes: number, more: Record<string, unknown> = {}) => ({ mediaId, mimeType, sizeBytes: String(sizeBytes), ...more });
const SEED_WAVEFORM = Array.from({ length: 64 }, (_, i) => Math.round(30 + 60 * Math.abs(Math.sin(i / 4))));
/** Phase 10c: one typed message per type (ids 30..39). */
export const GROUP_SEED_TYPED: GroupSeedMessage[] = [
  { id: 'group-seed-msg-30', role: 'USER', content: '📷 The venue', author: human('ms-dana'), metadata: { groupContent: { type: 'IMAGE', caption: 'The venue', media: media('seed-image~mv2.png', 'image/png', 182_000, { fileName: 'venue.png', width: 1200, height: 800 }) } } },
  { id: 'group-seed-msg-31', role: 'USER', content: '🎥 Video', author: human('ms-amir'), metadata: { groupContent: { type: 'VIDEO', media: media('seed-video~mv2.mp4', 'video/mp4', 4_200_000, { fileName: 'walkthrough.mp4', width: 1280, height: 720, durationMs: 42_000 }) } } },
  { id: 'group-seed-msg-32', role: 'USER', content: '📄 Run of show', author: human('ms-noa'), metadata: { groupContent: { type: 'FILE', caption: 'Run of show', media: media('seed-doc~mv2.pdf', 'application/pdf', 820_000, { fileName: 'Launch run of show.pdf' }) } } },
  { id: 'group-seed-msg-33', role: 'USER', content: '🎤 Voice message (0:14)', author: human('ms-dana'), metadata: { groupContent: { type: 'VOICE_NOTE', media: media('seed-voice~mv2.m4a', 'audio/mp4', 120_000, { durationMs: 14_000, waveform: SEED_WAVEFORM }) } } },
  { id: 'group-seed-msg-34', role: 'USER', content: '👾 GIF', author: human('ms-amir'), metadata: { groupContent: { type: 'GIF', media: media('seed-gif~mv2.gif', 'image/gif', 900_000, { width: 480, height: 270 }) } } },
  {
    id: 'group-seed-msg-35', role: 'USER', content: '📊 Poll: Which day works for the launch?', author: human('ms-noa'),
    metadata: {
      groupContent: { type: 'POLL', poll: { question: 'Which day works for the launch?', options: [{ id: '1', text: 'Tuesday' }, { id: '2', text: 'Wednesday' }, { id: '3', text: 'Thursday' }] } },
      groupPollVotes: [{ metaSiteId: 'ms-dana', optionIds: ['1'] }, { metaSiteId: 'ms-amir', optionIds: ['2'] }, { metaSiteId: 'ms-ronny', optionIds: ['1'] }],
    },
  },
  {
    id: 'group-seed-msg-36', role: 'USER', content: '📅 Event: Launch party', author: human('ms-dana'),
    metadata: {
      groupContent: { type: 'EVENT', event: { title: 'Launch party', startDate: '2026-11-10T17:00:00.000Z', endDate: '2026-11-10T20:00:00.000Z', location: 'Rooftop, 12 Rothschild Blvd', description: 'Drinks and a short demo. Bring a friend.' } },
      groupEventRsvps: [{ metaSiteId: 'ms-amir', response: 'GOING' }, { metaSiteId: 'ms-noa', response: 'MAYBE' }],
    },
  },
  { id: 'group-seed-msg-37', role: 'USER', content: '📍 Café Noir', author: human('ms-amir'), metadata: { groupContent: { type: 'LOCATION', location: { latitude: 32.0636, longitude: 34.7748, label: 'Café Noir', address: '43 Ahad Ha\'Am St, Tel Aviv' } } } },
  { id: 'group-seed-msg-38', role: 'USER', content: '👤 Contact: Yael Bar', author: human('ms-noa'), metadata: { groupContent: { type: 'CONTACT', contact: { name: 'Yael Bar', phones: ['+972 50 123 4567'], emails: ['yael@example.com'] } } } },
  { id: 'group-seed-msg-39', role: 'USER', content: '🥳 Sticker', author: human('ms-dana'), metadata: { groupContent: { type: 'STICKER', stickerId: 'party' } } },
];

export const GROUP_SEED_TRANSCRIPT: GroupSeedMessage[] = [
  { id: 'group-seed-msg-1', role: 'SYSTEM', content: '', groupEvent: { type: 'JOINED', participantId: 'ms-dana', displayName: 'Dana Levi' } },
  { id: 'group-seed-msg-2', role: 'USER', content: 'Hi all! Can we move the launch to Tuesday?', author: human('ms-dana') },
  { id: 'group-seed-msg-3', role: 'USER', content: 'Marketing needs one more day for the assets.', author: human('ms-dana') },
  { id: 'group-seed-msg-4', role: 'USER', content: 'Works for me.', author: human('ms-ronny') },
  { id: 'group-seed-msg-5', role: 'SYSTEM', content: '', groupEvent: { type: 'MAESTRO_ADDED', participantId: 'agent-atlas', displayName: 'Atlas' } },
  {
    id: 'group-seed-msg-6',
    role: 'ASSISTANT',
    content: 'Tuesday at 10:00 is free for everyone. Want me to move the invite?',
    author: { principalMetaSiteId: 'agent-atlas', kind: 'MAESTRO', agentOwnerMetaSiteId: 'ms-dana' },
  },
  { id: 'group-seed-msg-7', role: 'USER', content: 'Yes please 👍', author: human('ms-amir') },
  { id: 'group-seed-msg-8', role: 'SYSTEM', content: '', groupEvent: { type: 'LEFT', participantId: 'ms-yael', displayName: 'Yael Shani' } },
  // Phase 5: an approval the viewer owns (Approve / Deny), one of Dana's that a newer card superseded (only the
  // APPROVED one shows), and two "can't help right now" notices.
  { id: 'group-seed-msg-9', role: 'USER', content: '@Maestro when is Ronny free on Thursday?', author: human('ms-amir') },
  {
    id: 'group-seed-msg-10', role: 'ASSISTANT', content: '', author: maestro('ms-ronny'),
    metadata: { groupApproval: { approvalId: 'appr-seed-1', agentOwnerMetaSiteId: 'ms-ronny', requesterMetaSiteId: 'ms-amir', operation: 'READ_CALENDAR', state: 'PENDING', expiresDate: inADay() } },
  },
  {
    id: 'group-seed-msg-11', role: 'ASSISTANT', content: '', author: maestro('ms-dana'),
    metadata: { groupApproval: { approvalId: 'appr-seed-2', agentOwnerMetaSiteId: 'ms-dana', requesterMetaSiteId: 'ms-ronny', operation: 'READ_FILES', state: 'PENDING', expiresDate: inADay() } },
  },
  {
    id: 'group-seed-msg-12', role: 'ASSISTANT', content: '', author: maestro('ms-dana'),
    metadata: { groupApproval: { approvalId: 'appr-seed-2', agentOwnerMetaSiteId: 'ms-dana', requesterMetaSiteId: 'ms-ronny', operation: 'READ_FILES', state: 'APPROVED', expiresDate: inADay() } },
  },
  { id: 'group-seed-msg-13', role: 'ASSISTANT', content: '', author: maestro('ms-amir'), metadata: { maestroUnavailable: { reason: 'OUT_OF_CREDITS' } } },
  { id: 'group-seed-msg-14', role: 'ASSISTANT', content: '', author: maestro('ms-dana'), metadata: { maestroUnavailable: { reason: 'PAUSED' } } },
  // Phase 7: three files shared into the group — Dana's editable plan, Amir's read-only budget, and a brief Noa
  // has since stopped sharing (it opens to the unavailable card). The same three on iOS / Android under
  // `SYMPHONY_CHAT_SEED_GROUP=files`.
  { id: 'group-seed-msg-15', role: 'USER', content: 'Here is the launch plan — edit away.', author: human('ms-dana'), attachments: [groupFileAttachment(0)] },
  { id: 'group-seed-msg-16', role: 'USER', content: 'Budget, read-only for now.', author: human('ms-amir'), attachments: [groupFileAttachment(1)] },
  { id: 'group-seed-msg-17', role: 'USER', content: 'The old brief.', author: human('ms-noa'), attachments: [groupFileAttachment(2)] },
  // Phase 8: Dana's Maestro asks the viewer's (a typed MAESTRO mention, one hop into the chain), a chain that paused at
  // a bound, and Amir's Maestro stopped by the viewer — the same three rows on iOS / Android.
  {
    id: 'group-seed-msg-18', role: 'ASSISTANT', content: '@Maestro is Ronny free on Tuesday at 10:00?', author: { principalMetaSiteId: 'agent-atlas', kind: 'MAESTRO', agentOwnerMetaSiteId: 'ms-dana' },
    metadata: { groupChain: { chainId: 'chain-seed-1', depth: 1, startedMs: 1_761_800_000_000 }, mentions: [{ metaSiteId: 'ms-ronny', kind: 'MAESTRO' }] },
  },
  {
    id: 'group-seed-msg-19', role: 'ASSISTANT', content: 'Paused here. Ask again to continue.', author: { principalMetaSiteId: 'ms-ronny', kind: 'SYSTEM' },
    metadata: { groupChainStopped: { chainId: 'chain-seed-1', reason: 'TOO_DEEP', maestroOwnerMetaSiteId: 'ms-dana' } },
  },
  {
    id: 'group-seed-msg-20', role: 'ASSISTANT', content: "Amir's Maestro was stopped. Anything it already did stays done.", author: { principalMetaSiteId: 'ms-ronny', kind: 'SYSTEM' },
    metadata: { groupWorkStopped: { maestroOwnerMetaSiteId: 'ms-amir', stoppedByMetaSiteId: 'ms-ronny' } },
  },
  // Phase 10c: one message of every type, from different members — the same ten on iOS / Android under
  // `SYMPHONY_CHAT_SEED_GROUP`. Media links come from `groupChatMockAnswer('media-urls')` (dev Mock data only).
  ...GROUP_SEED_TYPED,
];

// ─── Phase 3b: the group actions, answered locally for the dev Mock data fixture ─────────────────────────────
//
// The dev Mock data layer hands a `POST /api/group-chat/<action>` whose body names a mock id (a mocked chat, or a
// `mock-invite-<n>`) to this function instead of the server, so every Phase 3b flow can be walked on the fixture.
// The invite ids pick the invite screen's states: 1 preview · 2 not for you · 3 expired · 4 cancelled · 5 group
// ended · 6 feature off.

const MOCK_LAUNCH_CONVERSATION = 'mock-conversation-group-1';
const isoAgo = (hours: number) => new Date(Date.now() - hours * 3_600_000).toISOString();

function mockMembers(summary: GroupSummary) {
  return summary.participants
    .filter((p) => p.kind === 'HUMAN')
    .map((p, i) => ({
      metaSiteId: p.id,
      name: p.displayName,
      avatarUrl: null,
      role: p.role,
      joinedDate: isoAgo(48 - i),
      historyFrom: null,
      you: p.id === summary.viewerId,
    }));
}

function mockInvite(inviteId: string, state: string, policy = 'FROM_DATE') {
  return {
    inviteId,
    conversationId: MOCK_LAUNCH_CONVERSATION,
    email: 'yael@example.com',
    name: 'Launch crew',
    historyPolicy: policy,
    historyFrom: policy === 'FROM_DATE' ? '2026-10-20T00:00:00.000Z' : null,
    state,
    expiresDate: isoAgo(-24 * 7),
    inviteLink: `https://symphony.wix.app/group-invite/${inviteId}`,
  };
}

/** The Maestro settings the fixture answers (Phase 5): the viewer's own Maestro is in "Launch crew", not in the
 *  unnamed group. A save bumps the revision, and a save on a stale revision is refused as STALE_SETTINGS. */
const MOCK_UNNAMED_CONVERSATION = 'mock-conversation-group-2';
const mockSettingsByConversation: Record<string, Record<string, unknown>> = {};
export function mockMaestroSettings(conversationId: string): Record<string, unknown> {
  mockSettingsByConversation[conversationId] ??= {
    joined: conversationId !== MOCK_UNNAMED_CONVERSATION,
    grants: [{ kind: 'CALENDAR_AVAILABILITY', paths: [] }, { kind: 'FILES_READ', paths: ['spaces/launch/files/plan.md'] }],
    perAskerDailyTurns: 20,
    groupDailyTurns: 60,
    paused: false,
    proactive: false,
    workStoppedDate: null,
    revision: '3',
    defaultPerAskerDailyTurns: 20,
    defaultGroupDailyTurns: 60,
    maxPerAskerDailyTurns: 200,
    maxGroupDailyTurns: 600,
  };
  return mockSettingsByConversation[conversationId];
}

export function groupChatMockAnswer(action: string, body: Record<string, unknown>): Record<string, unknown> {
  const conversationId = typeof body.conversationId === 'string' ? body.conversationId : MOCK_LAUNCH_CONVERSATION;
  const summary = conversationId === MOCK_UNNAMED_CONVERSATION ? GROUP_SEED_UNNAMED : GROUP_SEED_LAUNCH;
  const group = {
    conversationId, name: summary.name ?? '', active: true, ownerMetaSiteId: 'ms-ronny', createdDate: isoAgo(72),
    // Phase 10a: the group's identity and settings (a save bumps the revision; a stale one is refused).
    description: mockGroupInfo.description, photoUrl: mockGroupInfo.photoUrl, settings: mockGroupSettings,
  };
  const inviteId = typeof body.inviteId === 'string' ? body.inviteId : '';
  switch (action) {
    case 'members':
      return {
        group,
        members: mockMembers(summary),
        maestros: summary.participants.filter((p) => p.kind === 'MAESTRO').map((p) => ({ ownerMetaSiteId: p.ownerId ?? p.id, name: p.displayName })),
        pendingInvites: [mockInvite('mock-invite-1', 'PENDING')],
        yourRole: 'OWNER',
        yourCapabilities: { canAddPeople: true, canEditInfo: true, canSendMessages: true, canModerate: true },
      };
    // Phase 10a. A link token containing "invalid" / "limited" / "approval" / "member" / "pending" picks the join
    // screen's state; any other joins the unnamed group.
    case 'update-settings': {
      const sent = (body.settings ?? {}) as Record<string, unknown>;
      if (String(sent.revision) !== mockGroupSettings.revision) return { refusal: 'STALE_SETTINGS', group };
      mockGroupSettings = { ...(sent as typeof mockGroupSettings), revision: String(Number(mockGroupSettings.revision) + 1) };
      return { group: { ...group, settings: mockGroupSettings } };
    }
    case 'update-info':
      if (typeof body.description === 'string') mockGroupInfo.description = body.description;
      if (typeof body.photoUrl === 'string') mockGroupInfo.photoUrl = body.photoUrl || null;
      return { group: { ...group, ...(typeof body.name === 'string' ? { name: body.name } : {}), description: mockGroupInfo.description, photoUrl: mockGroupInfo.photoUrl } };
    case 'invite-link':
    case 'reset-invite-link': {
      if (action === 'reset-invite-link') mockLinkSecret += 1;
      const token = `${conversationId}.Mock${mockLinkSecret}Secret`;
      return { link: { url: `https://symphony.wix.app/group-join/${token}`, qrUrl: `https://symphony.wix.app/group-join/${token}`, token, createdDate: isoAgo(1) } };
    }
    case 'preview-link': {
      const token = String(body.token ?? '');
      if (token.includes('invalid')) return { refusal: 'INVITE_LINK_INVALID' };
      if (token.includes('limited')) return { refusal: 'JOIN_RATE_LIMITED' };
      return { preview: { conversationId: MOCK_UNNAMED_CONVERSATION, name: 'Launch crew', photoUrl: null, memberCount: 4, joinApproval: token.includes('approval'), alreadyMember: token.includes('member'), requestPending: token.includes('pending') } };
    }
    case 'join-by-link':
      return String(body.token ?? '').includes('approval')
        ? { joined: false, pendingApproval: true, group: null }
        : { joined: true, pendingApproval: false, group: { ...group, conversationId: MOCK_UNNAMED_CONVERSATION } };
    case 'join-requests':
      return { requests: mockJoinRequests };
    case 'resolve-join-request': {
      const known = mockJoinRequests.some((r) => r.requestId === body.requestId);
      mockJoinRequests = mockJoinRequests.filter((r) => r.requestId !== body.requestId);
      return known ? { resolved: true, approved: body.approve === true, member: null } : { refusal: 'JOIN_REQUEST_UNAVAILABLE' };
    }
    case 'delete-message':
      return {
        message: {
          id: body.messageId, conversationId, role: 'USER', content: '', createdDate: isoAgo(1),
          author: { principalMetaSiteId: summary.viewerId, kind: 'HUMAN' },
          metadata: { groupDeleted: { deletedByMetaSiteId: summary.viewerId, deletedDate: new Date().toISOString() } },
        },
      };
    case 'report-group':
    case 'report-message':
      return { reported: true, reportId: 'mock-report-1', left: body.leave === true };
    case 'block': {
      const p = summary.participants.find((x) => x.id === body.metaSiteId);
      const blocked = { metaSiteId: String(body.metaSiteId), name: p?.displayName ?? '', avatarUrl: null, blockedDate: new Date().toISOString() };
      mockBlocked = [blocked, ...mockBlocked.filter((b) => b.metaSiteId !== blocked.metaSiteId)];
      return { blocked };
    }
    case 'unblock':
      mockBlocked = mockBlocked.filter((b) => b.metaSiteId !== body.metaSiteId);
      return { unblocked: true };
    case 'blocked':
      return { blocked: mockBlocked };
    case 'maestro-settings':
      return { settings: mockMaestroSettings(conversationId) };
    case 'update-maestro-settings': {
      const current = mockMaestroSettings(conversationId);
      const sent = (body.settings ?? {}) as Record<string, unknown>;
      if (String(sent.revision) !== String(current.revision)) return { refusal: 'STALE_SETTINGS' };
      const saved = { ...current, ...sent, revision: String(Number(current.revision) + 1) };
      mockSettingsByConversation[conversationId] = saved;
      return { settings: saved };
    }
    case 'join-maestro': {
      const joined = { ...mockMaestroSettings(conversationId), joined: true };
      mockSettingsByConversation[conversationId] = joined;
      return { settings: joined };
    }
    case 'remove-maestro':
      return { removed: true };
    case 'stop-maestro-work':
      return { stopped: true, stoppedDate: new Date().toISOString() };
    case 'resolve-approval':
      return { approved: body.approve === true };
    case 'create-invites': {
      const emails = Array.isArray(body.emails) ? (body.emails as string[]) : [];
      const unknown = emails.filter((e) => e.includes('unknown'));
      if (unknown.length > 0 && body.confirmUnknown !== true) {
        return { requiresConfirmation: true, unknownEmails: unknown, group: null, outcomes: [] };
      }
      return {
        requiresConfirmation: false,
        unknownEmails: [],
        group,
        outcomes: emails.map((email, i) => ({
          email,
          result: email.startsWith('dana') ? 'ALREADY_MEMBER' : i % 2 === 0 ? 'INVITED' : 'ALREADY_INVITED',
          notified: i === 0,
          invite: mockInvite(`mock-invite-${i + 1}`, 'PENDING', String(body.historyPolicy ?? 'ALL')),
        })),
      };
    }
    case 'invite':
      switch (inviteId) {
        case 'mock-invite-1': return { invite: mockInvite(inviteId, 'PENDING'), group, inviterName: 'Ronny Elkayam', addressedToYou: true };
        case 'mock-invite-2': return { refusal: 'INVITE_NOT_FOR_YOU' };
        case 'mock-invite-3': return { refusal: 'INVITE_EXPIRED' };
        case 'mock-invite-4': return { refusal: 'INVITE_REVOKED' };
        case 'mock-invite-5': return { refusal: 'GROUP_ENDED' };
        case 'mock-invite-6': return { refusal: 'FEATURE_OFF' };
        default: return { refusal: 'INVITE_UNAVAILABLE' };
      }
    case 'accept-invite':
      // Lands in the unnamed group, where the viewer's own Maestro is not in yet: the gentle prompt shows there.
      return inviteId === 'mock-invite-1'
        ? { group: { ...group, conversationId: MOCK_UNNAMED_CONVERSATION, name: '' }, member: { metaSiteId: 'ms-yael', name: 'Yael Shani', avatarUrl: null, role: 'MEMBER', joinedDate: new Date().toISOString(), historyFrom: null, you: true } }
        : { refusal: 'INVITE_UNAVAILABLE' };
    case 'revoke-invite':
      return { invite: mockInvite(inviteId, 'REVOKED') };
    case 'rename':
      return { group: { ...group, name: typeof body.name === 'string' ? body.name : '' } };
    case 'set-role':
      return { member: { ...mockMembers(summary).find((m) => m.metaSiteId === body.memberMetaSiteId), role: body.role } };
    case 'remove-member':
      return { removed: true };
    case 'leave':
      return { refusal: 'OWNER_MUST_END' };
    case 'end':
      return { group: { ...group, active: false }, removedMembers: summary.participants.filter((p) => p.kind === 'HUMAN').length - 1 };
    case 'viewer-state':
      return { viewerState: { pinned: body.pinned === true, muted: body.muted === true, archived: body.archived === true, hasUnread: false, lastReadDate: null, lastReadMessageId: null } };
    case 'post-message':
      return {
        message: {
          id: body.clientMessageId,
          conversationId,
          role: 'USER',
          content: body.text,
          createdDate: new Date().toISOString(),
          author: { principalMetaSiteId: summary.viewerId, kind: 'HUMAN' },
          metadata: {
            groupPost: true,
            ...(Array.isArray(body.mentionedMetaSiteIds) && body.mentionedMetaSiteIds.length ? { mentions: body.mentionedMetaSiteIds } : {}),
            ...(body.replyToMessageId ? { replyToMessageId: body.replyToMessageId } : {}),
          },
        },
      };
    // Phase 7: files shared into the group. The revoked brief answers FILE_REVOKED on open; a save whose text says
    // "conflict" answers FILE_EDIT_CONFLICT, so the reload-and-merge prompt can be walked.
    case 'files':
      return { files: GROUP_SEED_FILES.map((f, i) => mockGroupFile(conversationId, f, i)) };
    case 'my-files':
      return { files: GROUP_SEED_FILES.map((f, i) => mockGroupFile(MOCK_LAUNCH_CONVERSATION, f, i)) };
    case 'read-file': {
      const i = GROUP_SEED_FILES.findIndex((f) => f.fileId === body.fileId);
      if (i < 0) return { refusal: 'FILE_NOT_SHARED' };
      if (!GROUP_SEED_FILES[i].available) return { refusal: 'FILE_REVOKED' };
      return { file: mockGroupFile(conversationId, GROUP_SEED_FILES[i], i), content: mockGroupFileContent[GROUP_SEED_FILES[i].fileId] ?? GROUP_SEED_FILES[i].content };
    }
    case 'write-file': {
      const i = GROUP_SEED_FILES.findIndex((f) => f.fileId === body.fileId);
      if (i < 0) return { refusal: 'FILE_NOT_SHARED' };
      if (GROUP_SEED_FILES[i].permission === 'READ') return { refusal: 'FILE_READ_ONLY' };
      if (typeof body.content === 'string' && body.content.includes('conflict')) return { refusal: 'FILE_EDIT_CONFLICT', file: null, currentEtag: 'mock-etag-2' };
      mockGroupFileContent[GROUP_SEED_FILES[i].fileId] = String(body.content ?? '');
      return { file: { ...mockGroupFile(conversationId, GROUP_SEED_FILES[i], i), etag: `mock-etag-saved-${Date.now()}` } };
    }
    case 'share-file':
      return {
        share: { conversationId, fileId: body.fileId, fileName: '', permission: body.permission ?? 'EDIT', active: true, sharedDate: new Date().toISOString() },
        messagePosted: true,
        message: {
          id: body.clientMessageId, conversationId, role: 'USER', content: typeof body.text === 'string' ? body.text : '', createdDate: new Date().toISOString(),
          author: { principalMetaSiteId: summary.viewerId, kind: 'HUMAN' },
          attachments: [{ fileName: 'Shared file', mimeType: 'text/markdown', url: '', groupFile: { conversationId, fileId: body.fileId, ownerMetaSiteId: summary.viewerId } }],
        },
      };
    case 'file-shares':
      // One active share in "Launch crew", one in a group the viewer has LEFT (no chats row: "A group chat"), one revoked.
      return {
        shares: [
          { conversationId: MOCK_LAUNCH_CONVERSATION, fileId: body.fileId, fileName: '', permission: 'EDIT', active: true, sharedDate: isoAgo(5) },
          { conversationId: 'mock-conversation-left-1', fileId: body.fileId, fileName: '', permission: 'READ', active: true, sharedDate: isoAgo(30) },
          { conversationId: MOCK_UNNAMED_CONVERSATION, fileId: body.fileId, fileName: '', permission: 'EDIT', active: false, sharedDate: isoAgo(90), revokedDate: isoAgo(60) },
        ],
      };
    case 'update-file-share':
      return { share: { conversationId, fileId: body.fileId, permission: body.permission, active: true } };
    case 'revoke-file-share':
      return { share: { conversationId, fileId: body.fileId, permission: 'EDIT', active: false, revokedDate: new Date().toISOString() } };
    // Phase 10b: the message actions, against a stand-in row (dev Mock data only).
    case 'react': {
      const emoji = String(body.emoji ?? '');
      return { message: mockRow(conversationId, String(body.messageId), summary.viewerId, { groupReactions: emoji ? [{ metaSiteId: summary.viewerId, emoji, date: new Date().toISOString() }] : [] }) };
    }
    case 'edit-message':
      return { message: { ...mockRow(conversationId, String(body.messageId), summary.viewerId, { groupEdited: { editedDate: new Date().toISOString(), count: 1 } }), content: body.text } };
    case 'delete-for-me':
      return { hidden: body.restore !== true };
    case 'pin': {
      const id = String(body.messageId);
      if (body.pinned === true && !mockPins.includes(id)) {
        if (mockPins.length >= 3 && body.replaceOldest !== true) return { refusal: 'PIN_LIMIT_REACHED', pinnedMessages: mockPins.map((m) => mockPin(conversationId, m)) };
        mockPins = [...mockPins, id].slice(-3);
      }
      if (body.pinned === false) mockPins = mockPins.filter((m) => m !== id);
      return { pinnedMessages: mockPins.map((m) => mockPin(conversationId, m)) };
    }
    case 'star':
      return { starred: body.starred === true };
    case 'starred':
      return { messages: [], nextCursor: null };
    case 'forward': {
      const targets = Array.isArray(body.targetConversationIds) ? body.targetConversationIds.map(String) : [];
      return { results: targets.map((id) => ({ conversationId: id, refusal: null, message: null, filesNotForwarded: 0 })) };
    }
    // Phase 10c: typed messages (dev Mock data only). The image links are this app's own illustrations.
    case 'media-urls': {
      const ids = Array.isArray(body.messageIds) ? body.messageIds.map(String) : [];
      const urls: Record<string, string> = { 'group-seed-msg-30': '/illustrations/home-empty-hero-wide.png', 'group-seed-msg-34': '/illustrations/agents-empty-hero.png' };
      return { urls: ids.filter((id) => urls[id]).map((id) => ({ messageId: id, url: urls[id], expiresDate: new Date(Date.now() + 10 * 60_000).toISOString() })) };
    }
    case 'media-upload':
      return { refusal: 'MEDIA_NOT_ALLOWED' };
    case 'poll-vote':
      return { message: mockRow(conversationId, String(body.messageId), summary.viewerId, { groupPollVotes: [...pollVotesWithout(String(body.messageId), summary.viewerId), ...(Array.isArray(body.optionIds) && body.optionIds.length ? [{ metaSiteId: summary.viewerId, optionIds: body.optionIds }] : [])] }) };
    case 'event-rsvp':
      return { message: mockRow(conversationId, String(body.messageId), summary.viewerId, { groupEventRsvps: [...rsvpsWithout(String(body.messageId), summary.viewerId), ...(body.response !== 'NO_RESPONSE' ? [{ metaSiteId: summary.viewerId, response: body.response }] : [])] }) };
    case 'message-info':
      return {
        message: mockRow(conversationId, String(body.messageId), summary.viewerId, {}),
        receipts: [
          { metaSiteId: 'ms-dana', name: 'Dana Levi', avatarUrl: null, readDate: isoAgo(1) },
          { metaSiteId: 'ms-amir', name: 'Amir Cohen', avatarUrl: null, readDate: null },
        ],
      };
    default:
      return { refusal: 'UNKNOWN' };
  }
}

const seedList = (id: string, key: string): Record<string, unknown>[] => ((GROUP_SEED_TRANSCRIPT.find((m) => m.id === id)?.metadata?.[key] as Record<string, unknown>[] | undefined) ?? []);
const pollVotesWithout = (id: string, viewer: string | undefined) => seedList(id, 'groupPollVotes').filter((v) => v.metaSiteId !== viewer);
const rsvpsWithout = (id: string, viewer: string | undefined) => seedList(id, 'groupEventRsvps').filter((v) => v.metaSiteId !== viewer);

// Phase 10b fixture state (dev Mock data only).
let mockPins: string[] = [];
function mockRow(conversationId: string, id: string, viewerId: string | undefined, metadata: Record<string, unknown>) {
  // The seeded row's own text and author, so an answered row does not blank the bubble it replaces.
  const seed = GROUP_SEED_TRANSCRIPT.find((m) => m.id === id);
  return {
    id, conversationId, role: seed?.role ?? 'USER', content: seed?.content ?? '', createdDate: isoAgo(1),
    author: seed?.author ?? { principalMetaSiteId: viewerId, kind: 'HUMAN' }, metadata: { ...(seed?.metadata ?? {}), ...metadata },
  };
}
function mockPin(conversationId: string, id: string) {
  return { message: { id, conversationId, role: 'USER', content: 'Pinned message', createdDate: isoAgo(2) }, pinnedByMetaSiteId: 'ms-ronny', pinnedDate: isoAgo(1) };
}

const mockGroupFileContent: Record<string, string> = {};
// Phase 10a fixture state (dev Mock data only).
let mockGroupSettings = { whoCanAddPeople: 'ADMINS_ONLY', whoCanEditInfo: 'ADMINS_ONLY', whoCanSendMessages: 'ALL_MEMBERS', joinApproval: false, revision: '1' };
const mockGroupInfo: { description: string; photoUrl: string | null } = { description: 'Everything for the Tuesday launch.', photoUrl: null };
let mockLinkSecret = 1;
let mockJoinRequests = [
  { requestId: '00000000-0000-4000-8000-0000000000a1', metaSiteId: 'ms-yael', name: 'Yael Shani', avatarUrl: null, requestedDate: isoAgo(3) },
  { requestId: '00000000-0000-4000-8000-0000000000a2', metaSiteId: 'ms-omer', name: 'Omer Katz', avatarUrl: null, requestedDate: isoAgo(1) },
];
let mockBlocked: { metaSiteId: string; name: string; avatarUrl: string | null; blockedDate: string | null }[] = [];
function mockGroupFile(conversationId: string, f: (typeof GROUP_SEED_FILES)[number], i: number) {
  return {
    conversationId,
    fileId: f.fileId,
    ownerMetaSiteId: f.ownerMetaSiteId,
    ownerName: f.ownerName,
    mine: false,
    permission: f.permission,
    available: f.available,
    unavailableReason: 'unavailableReason' in f ? f.unavailableReason : null,
    fileName: f.fileName,
    contentType: f.fileName.endsWith('.csv') ? 'text/csv' : 'text/markdown',
    sizeBytes: f.content.length,
    sharedDate: isoAgo(i + 1),
    updatedDate: isoAgo(i + 1),
    contentHead: f.content.slice(0, 2000),
    contentTruncated: false,
    referenceUrl: null,
    hint: null,
    etag: f.available ? 'mock-etag-1' : null,
  };
}
